import React from "react";
import { Card, CardContent, CardHeader, Grid, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { formatNumber, numberPrecision } from "../../../../helpers/UtilityHelper";
import BarChartIcon from '@mui/icons-material/BarChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import { Box } from "@mui/system";
import _ from 'lodash'
import { ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import ChartTooltip from "../../../charts/ChartTooltip";
import ChartEmpty from "../../../charts/ChartEmpty";
import InfoBox from "../../../../common/InfoBox";
import MaterialTable from "../../../../common/MaterialTable";
import { STATUS_COLORS } from "../../../charts/colors";

export default function DeliveryAttempt({ data }) {
    const [view, setView] = React.useState("chart");
    const tatColors = {
        firstAttempt: STATUS_COLORS.DELIVERED,
        secondAttempt: STATUS_COLORS.INTRANSIT,
        thirdAttempt: STATUS_COLORS.DELIVERY_FAILED,
        moreThanThreeAttempt: STATUS_COLORS.RTO,
    };
    const valueRender = (key) => {
        return (value) => {
            const keyValue = value[key];
            if (!keyValue) return '-';
            const percetange = value[`${key}_Percentage`];
            return `${keyValue} (${numberPrecision(percetange, 0)}%)`
        }
    }
    const tableColumns = [
        {
            label: 'Attempt',
            value: 'name',
            align: 'left'
        },
        {
            label: '1st Attempt',
            value: valueRender('firstAttempt'),
            align: 'left'
        },
        {
            label: '2nd Attempt',
            value: valueRender('secondAttempt'),
            align: 'left'
        },
        {
            label: '3rd Attempt',
            value: valueRender('thirdAttempt'),
            align: 'left'
        },
        {
            label: '3+ Attempt',
            value: valueRender('moreThanThreeAttempt'),
            align: 'left',
        },
    ]
    const attemptKeys = [
        { key: 'firstAttempt', label: '1st Attempt', color: tatColors.firstAttempt },
        { key: 'secondAttempt', label: '2nd Attempt', color: tatColors.secondAttempt },
        { key: 'thirdAttempt', label: '3rd Attempt', color: tatColors.thirdAttempt },
        { key: 'moreThanThreeAttempt', label: '3+ Attempt', color: tatColors.moreThanThreeAttempt },
    ]
    const chartData = (data || []).map((row) => ({
        ...row,
        _stackTotal: attemptKeys.reduce((s, k) => s + (row[k.key] || 0), 0),
    }))
    const totalFirstAttempt = isNaN(_.sumBy(data, 'firstAttempt'))
        ? 0
        : _.sumBy(data, 'firstAttempt');

    const totalSecondAttempt = isNaN(_.sumBy(data, 'secondAttempt'))
        ? 0
        : _.sumBy(data, 'secondAttempt');

    const totalThirdAttempt = isNaN(_.sumBy(data, 'thirdAttempt'))
        ? 0
        : _.sumBy(data, 'thirdAttempt');

    const totalMoreThanThreeAttempt = isNaN(_.sumBy(data, 'moreThanThreeAttempt'))
        ? 0
        : _.sumBy(data, 'moreThanThreeAttempt');

    const total = totalFirstAttempt + totalSecondAttempt + totalThirdAttempt + totalMoreThanThreeAttempt;
    const totalFirstAttemptPercentage = totalFirstAttempt / total * 100;
    const totalSecondAttemptPercentage = totalSecondAttempt / total * 100;
    const totalThirdAttemptPercentage = totalThirdAttempt / total * 100;
    const totalMoreThanThreeAttemptPercentage = totalMoreThanThreeAttempt / total * 100;
    const summary = [
        { header: '1st Attempt', value: `${formatNumber(totalFirstAttempt)}(${numberPrecision(totalFirstAttemptPercentage)}%)` },
        { header: '2nd Attempt', value: `${formatNumber(totalSecondAttempt)}(${numberPrecision(totalSecondAttemptPercentage)}%)` },
        { header: '3rd Attempt', value: `${formatNumber(totalThirdAttempt)}(${numberPrecision(totalThirdAttemptPercentage)}%)` },
        { header: '3+ Attempt', value: `${formatNumber(totalMoreThanThreeAttempt)}(${numberPrecision(totalMoreThanThreeAttemptPercentage)}%)` },
    ]
    return (
        <Card elevation={1}>
            <CardHeader title='First Attempt Delivery Success Rate' action={(
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ToggleButtonGroup color="primary" size="small" exclusive onChange={(_e, v) => setView(v)} value={view}>
                        <ToggleButton value="chart" title="View Chart">
                            <BarChartIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton value="table" title="View Table">
                            <TableChartIcon fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )} />
            <CardContent>
                <Grid container spacing={2}>
                    {summary.map((sum) => (
                        <Grid item xs={6} md={3} key={sum.header}>
                            <InfoBox {...sum} />
                        </Grid>
                    ))}
                    {(view === 'chart') && (
                        <Grid item xs={12}>
                            {(!chartData || chartData.length === 0) ? <ChartEmpty height={380} /> : (
                            <ResponsiveContainer width="100%" height={380}>
                                <ComposedChart data={chartData} margin={{ top: 30, right: 20, bottom: 5, left: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#dee2e6" />
                                    <XAxis dataKey="name" fontSize="11px" fontWeight="500" />
                                    <YAxis fontSize="11px" fontWeight="500" label={{ value: 'Shipments', angle: -90, position: 'insideLeft', fontSize: 11, fill: '#666', textAnchor: 'middle' }} />
                                    <Tooltip content={<ChartTooltip />} />
                                    <Legend verticalAlign="top" align="left" iconSize="10px" iconType="circle" wrapperStyle={{ fontSize: 11, fontWeight: 500, paddingBottom: 10 }} />
                                    {attemptKeys.map((k, i) => {
                                        const isLast = i === attemptKeys.length - 1
                                        return (
                                            <Area
                                                key={k.key}
                                                dataKey={k.key}
                                                name={k.label}
                                                stackId="attempts"
                                                type="monotone"
                                                stroke={k.color}
                                                fill={k.color}
                                                fillOpacity={0.85}
                                                strokeWidth={1}
                                                animationEasing="ease-in-out"
                                                animationDuration={1000}
                                            >
                                                {isLast && (
                                                    <LabelList dataKey="_stackTotal" position="top" style={{ fontSize: 10, fontWeight: 600, fill: '#374151' }} formatter={(v) => (v == null || v === 0) ? '' : v} />
                                                )}
                                            </Area>
                                        )
                                    })}
                                </ComposedChart>
                            </ResponsiveContainer>
                            )}
                        </Grid>
                    )}
                    {(view === 'table') && (
                        <Grid item xs={12}>
                            <MaterialTable exportExcel={false} size="small" rows={data} columns={tableColumns} frozenColumnsCount={1} />
                        </Grid>
                    )}
                </Grid>
            </CardContent>
        </Card>
    )
}